import { useEffect, useState } from "react";
import dynamic from "next/dynamic";
const App = dynamic(() => import("../components/App"));
import Head from "next/head";
import axios from "axios";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { PreloadThumborImage } from "../utils/thumborjs";
import { getItemWithExpiry } from "../utils/localstorage";
import { useInView } from "react-intersection-observer";

import { Api } from "../networks/Api";
import { useRouter } from "next/router";
import HeroBanner from "../components/Home/HeroBanner";

const imageUrl = process.env.NEXT_PUBLIC_IMAGE_URL;

const Empty = () => {
  return (
    <div className="component-product-swipper relative ">
      <div className="flex justify-between h-[38px]">
        <div className="bg-gray-200 animate-pulse h-[32px] w-[79px] rounded" />
        <div className="bg-gray-200 animate-pulse h-[32px] w-[79px] rounded" />
      </div>

      <div className="swiper-container overflow-hidden flex gap-[10px]">
        {Array.from({ length: size }, (_, index) => index).map((v) => {
          return (
            <div key={`empty-${v}`} className="card-product shrink-0">
              <div className="card-product-img">
                <div className="bg-gray-200 animate-pulse aspect-square w-full" />
              </div>
              <div className="card-product-detail">
                <div className="space-y-1 title3line">
                  <div className="bg-gray-200 animate-pulse h-3 w-full rounded" />
                  <div className="bg-gray-200 animate-pulse h-3 w-full rounded" />
                  <div className="bg-gray-200 animate-pulse h-3 w-3/4 rounded" />
                </div>

                <div className="space-y-1 price-with-discount">
                  <div className="bg-gray-200 animate-pulse h-3 w-1/2 rounded" />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Component
const BannerCarousel = dynamic(() => import("../components/Home/Banner"), {
  ssr: false,
  loading: () => <div />,
});
const ProductSlide = dynamic(() => import("../components/ProductSlideScroll"), {
  ssr: false,
  loading: () => <Empty />,
});
const SocialSlideScroll = dynamic(
  () => import("../components/SocialSlideScroll"),
  { ssr: false, loading: () => <div /> },
);
const Brand = dynamic(() => import("../components/Home/Brand"), {
  ssr: true,
  loading: () => <div />,
});
const Firstcontent = dynamic(() => import("../components/Home/Firstcontent"), {
  ssr: true,
  loading: () => <div />,
});
const Secondcontent = dynamic(
  () => import("../components/Home/Secondcontent"),
  {
    ssr: true,
    loading: () => <div />,
  },
);
const Thirdcontent = dynamic(() => import("../components/Home/Thirdcontent"), {
  ssr: true,
  loading: () => <div />,
});
const CategoryGender = dynamic(
  () => import("../components/Home/CategoryGender"),
  {
    // SSR: ให้ category grid (ลิงก์หมวดหมู่) อยู่ใน HTML แรกเพื่อ crawlability
    ssr: true,
    loading: () => <div />,
  },
);
const FAQ = dynamic(() => import("../components/Home/FAQ"), {
  ssr: true,
  loading: () => <div />,
});

const page = 1;
const size = 8; // หน้าแรกเป็นสไลด์ ไม่ต้องโหลด 20 ตัว ลด payload/HTML บน mobile

const DeferredSection = ({ children, className = "", minHeight = 320 }) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0,
    rootMargin: "300px 0px",
  });

  return (
    <div ref={ref} className={className} style={{ minHeight }}>
      {inView ? children : null}
    </div>
  );
};

export async function getStaticProps(ctx) {
  let brandList = [];
  let bannerTop = [];
  let recommendedProducts = [];
  let newProducts = [];
  let bestProducts = [];
  let firstContent = [];
  let secondContent = [];
  let thirdContent = [];
  let socialContent = [];
  let bannerMiddle = [];
  let brandHomeList = [];
  let categoryGenderList = [];

  try {
    const [
      brandListResponse,
      bannerTopResponse,
      recommendedProductsResponse,
      newProductsResponse,
      // bestProductsResponse,
      firstContentResponse,
      secondContentResponse,
      thirdContentResponse,
      socialContentResponse,
      bannerMiddleResponse,
      brandHomeListResponse,
      categoryGenderResponse,
    ] = await Promise.all([
      axios.get(`${Api.getFeedBrandList}`, Api.Header()),
      axios.get(`${Api.getFeedBannerList}/top`, Api.Header()),
      axios.get(
        `${Api.getFeedProductList}?page=${page}&size=${size}&type=recommend`,
        Api.Header(),
      ),
      axios.get(
        `${Api.getFeedProductList}?page=${page}&size=${size}&type=new`,
        Api.Header(),
      ),
      // axios.get(
      //   `${Api.getFeedProductList}?page=${page}&size=${size}&type=best`,
      //   Api.Header(),
      // ),
      axios.get(`${Api.contentHomepage}?type=firstcontent`, Api.Header()),
      axios.get(`${Api.contentHomepage}?type=secondcontent`, Api.Header()),
      axios.get(`${Api.contentHomepage}?type=thirdcontent`, Api.Header()),
      axios.get(`${Api.contentHomepage}?type=socialcontent`, Api.Header()),
      axios.get(`${Api.getFeedBannerList}/middle`, Api.Header()),
      axios.get(`${Api.getFeedBrandHome}?page=1&size=16`, Api.Header()),
      axios.get(`${Api.getCategeryGender}`, Api.Header()),
    ]);

    if (brandListResponse?.status == 200) {
      // แปลงโครงสร้างข้อมูล
      brandList = brandListResponse?.data?.data?.reduce((acc, item) => {
        acc[item.number] = item.name;
        return acc;
      }, {});
    }

    if (bannerTopResponse?.status == 200) {
      bannerTop = bannerTopResponse?.data?.data;
    }
    if (recommendedProductsResponse?.status === 200) {
      recommendedProducts = recommendedProductsResponse?.data?.data;
    }
    if (newProductsResponse?.status === 200) {
      newProducts = newProductsResponse?.data?.data;
    }
    // if (bestProductsResponse?.status === 200) {
    //   bestProducts = bestProductsResponse?.data?.data;
    // }
    if (firstContentResponse?.status === 200) {
      firstContent = firstContentResponse?.data?.data;
    }
    if (secondContentResponse?.status === 200) {
      secondContent = secondContentResponse?.data?.data;
    }
    if (thirdContentResponse?.status === 200) {
      thirdContent = thirdContentResponse?.data?.data;
    }
    if (socialContentResponse?.status === 200) {
      socialContent = socialContentResponse?.data?.data;
    }
    if (bannerMiddleResponse?.status === 200) {
      bannerMiddle = bannerMiddleResponse?.data?.data;
    }
    if (brandHomeListResponse?.status === 200) {
      brandHomeList = brandHomeListResponse?.data?.data;
    }
    if (categoryGenderResponse?.status === 200) {
      categoryGenderList = categoryGenderResponse?.data?.data;
    }
  } catch (e) {
    console.log(e);
  }

  return {
    props: {
      ...(await serverSideTranslations(ctx?.locale, ["common"])),
      brandList,
      bannerTop,
      recommendedProducts,
      newProducts,
      bestProducts,
      firstContent,
      secondContent,
      thirdContent,
      socialContent,
      bannerMiddle,
      brandHomeList,
      categoryGenderList,
    },
    revalidate: 60,
  };
}

const Home = ({
  categoryMenu,
  hasSale,
  hasLive,
  dataSeo,
  dataReferrer,
  brandList,
  bannerTop,
  recommendedProducts,
  newProducts,
  bestProducts,
  firstContent,
  secondContent,
  thirdContent,
  socialContent,
  bannerMiddle,
  brandHomeList,
  categoryGenderList,
}) => {
  const router = useRouter();
  const { locale } = router;
  let queryParam = router?.query?.aid ? `aid=${router?.query?.aid}` : ``;
  queryParam = router?.query?.pid
    ? `${queryParam}&pid=${router?.query?.pid}`
    : queryParam;

  queryParam = router?.query?.pf
    ? `${queryParam}pf=${router?.query?.pf}`
    : queryParam;

  if (router?.query?.utm_source) {
    queryParam = queryParam
      ? `${queryParam}&utm_source=${router?.query?.utm_source}`
      : `?utm_source=${router?.query?.utm_source}`;
  }

  const [token, setToken] = useState(null);

  useEffect(() => {
    const storedToken = getItemWithExpiry("token");
    setToken(storedToken);
  }, []);

  const image_path =
    bannerTop?.length >= 1
      ? bannerTop[0]?.website_image_path?.replace(/\/uploads/g, "")
      : "";

  const image_path_m =
    bannerTop?.length >= 1
      ? bannerTop[0]?.mobile_image_path?.replace(/\/uploads/g, "")
      : "";

  const mobileFirstImage = image_path_m ? `${imageUrl}${image_path_m}` : "";
  const desktopFirstImage = image_path ? `${imageUrl}${image_path}` : "";

  return (
    <App
      token={token}
      categoryMenu={categoryMenu}
      hasSale={hasSale}
      hasLive={hasLive}
      dataSeo={dataSeo}
      dataReferrer={dataReferrer}
      brandList={brandList}
    >
      <Head>
        {process.env.NEXT_PUBLIC_APP_ENV == "production" ? (
          <meta
            name="google-site-verification"
            content="dloZRLwFVKf-XdY_u3X1JJ4CgYf6tReRWP3Q9wmt3qk"
          />
        ) : (
          <></>
        )}
        {/* <link
          rel="preload"
          as="image"
          href={isMobile ? mobileFirstImage : desktopFirstImage}
        /> 
        {isMobile ? (
          <PreloadThumborImage
            imagePath={mobileFirstImage}
            width={480}
            ratio={480 / 359}
          />
        ) : (
          <PreloadThumborImage
            imagePath={desktopFirstImage}
            width={1024}
            ratio={1024 / 324}
          />
        )}
        */}
        <PreloadThumborImage
          imagePath={mobileFirstImage}
          width={480}
          ratio={480 / 359}
          media="(max-width: 639px)"
        />
        <PreloadThumborImage
          imagePath={desktopFirstImage}
          width={1024}
          ratio={1024 / 324}
          media="(min-width: 640px)"
        />
      </Head>

      <main>
        <div className="relative mySwiper-banner sm:container sm:mx-auto">
          <HeroBanner
            bannerData={bannerTop}
            queryParam={queryParam}
          />
        </div>
        <div className="container mx-auto">
          <section className="intro container mx-auto px-4 py-8 text-center lg:px-0">
            {locale === "th" ? (
              <>
                <h1 className="text-xl font-semibold">
                  Luxury29 ร้านสินค้าแฟชั่นและลักชัวรีของแท้ในประเทศไทย
                </h1>
                <p className="text-sm">
                  Luxury29 เป็นร้านจำหน่ายสินค้าแบรนด์เนมและสินค้าลักชัวรี่ของแท้ในประเทศไทย
                  โดยจำหน่ายสินค้าใหม่จากแบรนด์ชั้นนำระดับโลก เช่น Hermès, Chanel,
                  Louis Vuitton, Dior, Gucci, Goyard และ Balenciaga ครอบคลุมกระเป๋า
                  รองเท้า เสื้อผ้า เครื่องประดับ และแฟชั่นแอ็กเซสซอรี
                  ลูกค้าสามารถเลือกซื้อสินค้าผ่านเว็บไซต์ แอปพลิเคชัน
                  และช่องทางออนไลน์ของ Luxury29 หรือเลือกชมสินค้าได้ที่หน้าร้านทั้ง 5
                  สาขาในประเทศไทย
                  พร้อมข้อมูลรายละเอียดสินค้าเพื่อช่วยให้ลูกค้าเปรียบเทียบสินค้าและตัดสินใจเลือกซื้อได้ง่ายขึ้น
                  ทั้งทางออนไลน์และหน้าร้าน
                </p>
              </>
            ) : (
              <>
                <h1 className="text-xl font-semibold">
                  Luxury29 – Fashion &amp; Luxury Retailer in Thailand
                </h1>
                <p className="mx-auto mt-4 max-w-4xl text-sm leading-7 text-gray-600">
                  Luxury29 is a retailer of authentic luxury and designer
                  products in Thailand, offering brand-new products from leading
                  global brands including Hermès, Chanel, Louis Vuitton, Dior,
                  Gucci, Goyard, and Balenciaga. Customers can shop for handbags,
                  shoes, clothing, jewelry, and fashion accessories through the
                  Luxury29 website, mobile application, and online channels, or
                  visit one of our 5 physical stores in Thailand. Luxury29
                  provides detailed product information to help customers compare
                  products and make informed purchasing decisions, with
                  convenient shopping options available both online and in-store.
                </p>
              </>
            )}
          </section>

          <DeferredSection
            className="flow-root [&>.component-product-swipper]:mb-0"
            minHeight={360}
          >
            <ProductSlide
              title={`New Arrivals`}
              url={`new${queryParam ? `?${queryParam}` : ``}`}
              products={newProducts}
              brandData={brandList}
            />
          </DeferredSection>

          {/* <ProductSlide
            title={`Best Seller`}
            url={`best${queryParam ? `?${queryParam}` : ``}`}
            products={bestProducts}
            brandData={brandList}
          /> */}

          {/* Category grid: render ฝั่ง server เสมอ (ไม่ gate ด้วย inView) เพื่อให้ลิงก์หมวดหมู่อยู่ใน HTML แรก */}
          {categoryGenderList?.length > 0 && (
            <div className="px-4 lg:px-0">
              <CategoryGender
                content={categoryGenderList}
                queryParam={queryParam}
              />
            </div>
          )}

          <DeferredSection className="flow-root" minHeight={380}>
            <ProductSlide
              title={`Recommended Products`}
              url={`recommend${queryParam ? `?${queryParam}` : ``}`}
              products={recommendedProducts}
              brandData={brandList}
            />
          </DeferredSection>

          <DeferredSection minHeight={320}>
            <div className="relative mySwiper-banner">
              <BannerCarousel
                bannerData={bannerMiddle}
                indexValue="website_image_path"
                mobileIndexValue="mobile_image_path"
                bannerWidth={1024}
                bannerHeight={324}
                queryParam={queryParam}
              />
            </div>
          </DeferredSection>
        </div>

        {firstContent && (
          <>
            <div className="container mx-auto my-5 px-4 lg:px-0">
              <Firstcontent content={firstContent} queryParam={queryParam} />
            </div>
            <div className="border-t border-gray-200 my-5" />
          </>
        )}

        {secondContent && (
          <>
            <div className="container mx-auto my-5 px-4 lg:px-0">
              <Secondcontent content={secondContent} queryParam={queryParam} />
            </div>
            <div className="border-t border-gray-200 my-5" />
          </>
        )}

        {thirdContent && (
          <>
            <div className="container mx-auto my-5 px-4 lg:px-0">
              <Thirdcontent content={thirdContent} queryParam={queryParam} />
            </div>
            <div className="border-t border-gray-200 my-5" />
          </>
        )}

        {brandHomeList?.length > 0 && (
          <div className="container mx-auto py-4">
            <Brand brands={brandHomeList} />
          </div>
        )}

        {socialContent?.length > 0 && (
          <DeferredSection className="bg-gray-100 flow-root" minHeight={344}>
            <div className="container mx-auto py-4">
              <SocialSlideScroll contents={socialContent} />
            </div>
          </DeferredSection>
        )}

        <div className="border-t border-gray-200">
          <FAQ queryParam={queryParam} />
        </div>
      </main>
    </App>
  );
};

export default Home;
